import { Button, Grid, Stack, Text } from "@mantine/core"
import { IconCirclePlus, IconX } from "@tabler/icons-react"
import { LabelWithTooltip, TextInput, Select } from "@tfg/ui"
import { useCallback, useEffect, useState } from "react"
import { z } from "zod"

import { FormFieldConfig } from "~/pages/Admin/components/TypesForm"
import { getNestedValue } from "~/utils/types"

const vatRegex = /^[A-Z]{2}\d{2}\d{9}$/
const eoriRegex = /^[A-Z]{2}\d{14}$/
const glnRegex = /^\d{13}$/
const dunsRegex = /^\d{9}$/
const rcsRegex = /^RCS .+ [AB] \d{9}$/

export const registrationIdSchema = z.discriminatedUnion("type", [
  z.object({
    id: z.string().optional(),
    type: z.literal("VAT"),
    value: z
      .string()
      .regex(vatRegex, {
        error:
          "VAT registration ID must be in format: country code (e.g., FR) + 2-digit key + 9-digit SIREN number.",
      })
      .describe(
        "VAT registration ID must be in format: country code (e.g., FR) + 2-digit key + 9-digit SIREN number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("EORI"),
    value: z
      .string()
      .regex(eoriRegex, {
        error:
          "EORI registration ID must be in format: country code (e.g., FR) + 14-digit number.",
      })
      .describe(
        "EORI registration ID must be in format: country code (e.g., FR) + 14-digit number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("GLN"),
    value: z
      .string()
      .regex(glnRegex, {
        error: "GLN registration ID must be in format: 13-digit number.",
      })
      .describe("GLN registration ID must be in format: 13-digit number."),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("DUNS"),
    value: z
      .string()
      .regex(dunsRegex, {
        error: "DUNS registration ID must be in format: 9-digit number.",
      })
      .describe("DUNS registration ID must be in format: 9-digit number."),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("RCS"),
    value: z
      .string()
      .regex(rcsRegex, {
        error:
          "RCS registration ID must be in format: RCS + space + company name + space + [A,B] + space + 9-digit number.",
      })
      .describe(
        "RCS registration ID must be in format: RCS + space + company name + space + [A,B] + space + 9-digit number."
      ),
  }),
  z.object({
    id: z.string().optional(),
    type: z.literal("OTHER"),
    value: z
      .string()
      .max(32, {
        error: "Other registration ID must be less than 32 characters.",
      })
      .describe("Other registration ID must be less than 32 characters."),
  }),
])

const SelectListRegistrationId = (fields: FormFieldConfig, form: any) => {
  const registrationId = getNestedValue(form.values, fields.name)
  const [options, setOptions] = useState<
    {
      label: string
      value: string
    }[]
  >(
    fields.options?.map((data) => ({
      label: data.label,
      value: data.value,
    })) || []
  )
  const [textInputValue, setTextInputValue] = useState<string>("")
  const [selectInputValue, setSelectInputValue] = useState<string>("")
  const [generalValue, setGeneralValue] = useState<
    {
      id?: string
      value: string
      type: string
    }[]
  >(
    registrationId
      ? registrationId.map(
          (item: { registrationId: string; type: string; id?: string }) => ({
            id: item?.id,
            value: item.registrationId,
            type: item.type,
          })
        )
      : []
  )
  const [error, setError] = useState<string | null>(null)

  const handleAdd = () => {
    const parsed = registrationIdSchema.safeParse({
      value: textInputValue,
      type: selectInputValue,
    })
    if (!parsed.success) {
      setError(parsed.error.issues[0].message)
      return
    }
    if (textInputValue && selectInputValue) {
      const newGeneralValue = [
        ...generalValue,
        { value: textInputValue, type: selectInputValue, id: undefined },
      ]
      setGeneralValue(newGeneralValue)
      form.setFieldValue(
        fields.name,
        newGeneralValue.map((item) => ({
          id: item?.id,
          registrationId: item.value,
          type: item.type,
        }))
      )
      setTextInputValue("")
      setSelectInputValue("")
      setError(null)
    }
  }

  const handleRemove = (value: string, type: string) => {
    const newGeneralValue = generalValue.filter(
      (item) => !(item.value === value && item.type === type)
    )
    setGeneralValue(newGeneralValue)

    form.setFieldValue(
      fields.name,
      newGeneralValue.map((item) => ({
        id: item?.id,
        registrationId: item.value,
        type: item.type,
      }))
    )
  }

  const reevaluateOptions = useCallback(() => {
    const newOptions =
      fields.options?.filter((data) => {
        if (data?.label === "OTHER") return true
        return !generalValue.map((item) => item.type).includes(data.value)
      }) || []
    setOptions(newOptions)
    setSelectInputValue(newOptions[0]?.value || "")
  }, [generalValue, fields.options])

  useEffect(() => {
    reevaluateOptions()
  }, [generalValue, reevaluateOptions])

  useEffect(() => {
    setError(null)
  }, [selectInputValue])

  return (
    <>
      <Stack gap={6} w="100%">
        {fields.tooltip && (
          <LabelWithTooltip
            label={fields.label}
            hoverHelperText={fields.tooltip}
            justify="space-between"
            fw={600}
            withAsterisk={fields.required ?? false}
          />
        )}
        <Grid align="center" w="100%">
          <Grid.Col span={8}>
            <TextInput
              key={fields.name}
              label={!fields.tooltip ? fields.label : undefined}
              labelProps={{
                fz: "xs",
                fw: 600,
              }}
              placeholder={fields.placeholder}
              value={textInputValue}
              onChange={(e) => setTextInputValue(e.target.value)}
              fz="xs"
              classNames={fields.customClass}
              disabled={fields.disabled}
            />
          </Grid.Col>
          <Grid.Col span={3}>
            <Select
              data={options}
              w="100%"
              value={selectInputValue}
              onChange={(e) => setSelectInputValue(e || "")}
              disabled={fields.disabled}
            />
          </Grid.Col>
          <Grid.Col span={1}>
            <Button
              type="button"
              variant="transparent"
              size="sm"
              onClick={handleAdd}
            >
              <IconCirclePlus size={20} />
            </Button>
          </Grid.Col>
        </Grid>
        <Grid align="start" w="100%">
          <Grid.Col span={10}>
            <Text fz="xs" c={error ? "red" : "dimmed"}>
              {error
                ? error
                : fields.options?.find(
                    (option) => option.value === selectInputValue
                  )?.description}
            </Text>
          </Grid.Col>
        </Grid>
        <Stack gap={0} style={{ borderLeft: "1px solid #e5e7eb" }}>
          {generalValue?.map((item: { value: string; type: string }) => (
            <Grid align="center" key={item.value + item.type}>
              <Grid.Col span={7}>
                <Text pl={10}>{item.value}</Text>
              </Grid.Col>
              <Grid.Col span={3}>
                <Text pl={12}>{item.type}</Text>
              </Grid.Col>
              <Grid.Col pl={8} span={2}>
                <Button
                  type="button"
                  variant="transparent"
                  color="red"
                  size="sm"
                  onClick={() => handleRemove(item.value, item.type)}
                >
                  <IconX size={16} />
                </Button>
              </Grid.Col>
            </Grid>
          ))}
        </Stack>
      </Stack>
    </>
  )
}

export default SelectListRegistrationId
